import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSeo } from '../lib/seo';
import { FilterIcon, ListIcon, MapIcon } from '../components/icons';
import { api, qs } from '../lib/api';
import {
  ALL_AMENITIES,
  AMENITY_LABEL,
  GENDER_LABEL,
  MODE_LABEL,
  today,
} from '../lib/format';
import { EmptyState, Field, ListingCard, Notice, Select, TextInput } from '../components/ui';
import { MapView } from '../components/Map';
import type { Facets, SearchResponse } from '../lib/types';
import { t } from '../lib/i18n';

/**
 * Search.
 *
 * Filters live in the URL, so a filtered search is a link someone can send to a
 * flatmate — and the back button behaves. Paging appends rather than replaces,
 * because a rental search is a scroll, not a set of numbered pages.
 */

const SORTS = [
  { value: 'recent', label: t("Plus récentes") },
  { value: 'price_asc', label: t("Prix croissant") },
  { value: 'price_desc', label: t("Prix décroissant") },
  { value: 'distance', label: t("Proximité du campus") },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [facets, setFacets] = useState<Facets | null>(null);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [pins, setPins] = useState<
    { id: string; title: string; lat: number; lng: number; priceFromCentimes: number }[]
  >([]);

  const filters = useMemo(
    () => ({
      q: params.get('q') ?? '',
      city: params.get('city') ?? '',
      mode: params.get('mode') ?? '',
      type: params.get('type') ?? '',
      maxPriceCentimes: params.get('maxPriceCentimes') ?? '',
      minPriceCentimes: params.get('minPriceCentimes') ?? '',
      guests: params.get('guests') ?? '',
      genderPolicy: params.get('genderPolicy') ?? '',
      furnished: params.get('furnished') ?? '',
      visitedOnly: params.get('visitedOnly') ?? '',
      verifiedHostOnly: params.get('verifiedHostOnly') ?? '',
      startDate: params.get('startDate') ?? '',
      endDate: params.get('endDate') ?? '',
      universityId: params.get('universityId') ?? '',
      maxDistanceMetres: params.get('maxDistanceMetres') ?? '',
      amenities: params.get('amenities') ?? '',
      sort: params.get('sort') ?? 'recent',
    }),
    [params],
  );

  /*
   * Mirrors the title the server computes for this same URL. Written out rather
   * than fetched so a filter change updates the tab immediately, without a
   * round trip for a string.
   */
  const modeWords: Record<string, string> = {
    COLOCATION: 'Colocations',
    MONTHLY: t("Chambres au mois"),
    NIGHTLY: t("Locations à la nuit"),
  };
  useSeo({
    title: filters.city
      ? t('{what} à {city}', { what: modeWords[filters.mode] ?? t('Colocations, chambres et locations'), city: t(filters.city) })
      : t('{what} au Maroc', { what: modeWords[filters.mode] ?? t('Colocations, chambres et locations') }),
    description: filters.city
      ? t('Annonces à {city} visitées par Bedly, hôtes vérifiés, prix tout compris.', { city: t(filters.city) })
      : null,
  });


  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value === '') next.delete(key);
      else next.set(key, value);
      next.delete('cursor');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const selectedAmenities = filters.amenities ? filters.amenities.split(',') : [];

  const toggleAmenity = (amenity: string) => {
    const next = selectedAmenities.includes(amenity)
      ? selectedAmenities.filter((item) => item !== amenity)
      : [...selectedAmenities, amenity];
    setFilter('amenities', next.join(','));
  };

  useEffect(() => {
    void api
      .get<Facets>('/search/facets')
      .then(setFacets)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const response = await api.get<SearchResponse>(`/search${qs({ ...filters, limit: 24 })}`);
        if (active) setResults(response);
      } catch {
        if (active) setResults({ items: [], nextCursor: null, hasMore: false });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [filters]);

  useEffect(() => {
    if (!showMap) return;
    void api
      .get<{ pins: typeof pins }>(
        `/search/map${qs({
          west: -17.5,
          south: 20.5,
          east: -0.9,
          north: 36.1,
          ...(filters.mode ? { mode: filters.mode } : {}),
          ...(filters.maxPriceCentimes ? { maxPriceCentimes: filters.maxPriceCentimes } : {}),
        })}`,
      )
      .then((response) => setPins(response.pins))
      .catch(() => undefined);
  }, [showMap, filters.mode, filters.maxPriceCentimes]);

  const loadMore = async () => {
    if (!results?.nextCursor) return;
    setLoadingMore(true);
    try {
      const next = await api.get<SearchResponse>(
        `/search${qs({ ...filters, limit: 24, cursor: results.nextCursor })}`,
      );
      setResults({
        items: [...results.items, ...next.items],
        nextCursor: next.nextCursor,
        hasMore: next.hasMore,
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => value !== '' && key !== 'sort',
  ).length;

  /*
   * Five filters answer almost every search: what, where, what kind of stay,
   * how much, and in what order. The other ten are real but occasional, and
   * showing all fifteen at once buries the five that matter.
   */
  const ADVANCED_KEYS = [
    'startDate',
    'endDate',
    'guests',
    'genderPolicy',
    'universityId',
    'maxDistanceMetres',
    'furnished',
    'visitedOnly',
    'verifiedHostOnly',
    'amenities',
  ] as const;

  const advancedActive = ADVANCED_KEYS.filter((key) => filters[key] !== '').length;

  const [showAllFilters, setShowAllFilters] = useState(advancedActive > 0);

  /*
   * A phone shows only the search box and the city — the two things almost
   * every search starts with — so type, budget and sort join the hidden set
   * there, and the button's count has to include them or a filtered list would
   * look unfiltered.
   */
  const mobileHiddenActive =
    advancedActive +
    (filters.mode ? 1 : 0) +
    (filters.maxPriceCentimes ? 1 : 0) +
    (filters.sort && filters.sort !== 'recent' ? 1 : 0);

  /*
   * Opened, never closed, by an active filter. Someone arriving on a shared
   * link with dates in it must see why the results are narrowed — hidden
   * filters silently shaping the list is the worst version of this control.
   * Closing it again is their decision, so this only ever opens.
   */
  useEffect(() => {
    if (advancedActive > 0) setShowAllFilters(true);
  }, [advancedActive]);

  return (
    <div className="page stack">
      <div className={`card card--pad stack filters ${showAllFilters ? 'filters--open' : ''}`}>
        <div className="row row--wrap filters__primary" style={{ gap: 10 }}>
          <div style={{ flex: '2 1 220px' }}>
            <Field label={t("Recherche libre")}>
              <TextInput
                value={filters.q}
                onChange={(value) => setFilter('q', value)}
                placeholder={t("Agdal, studio, proche tram…")}
              />
            </Field>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <Field label={t("Ville")}>
              <Select
                value={filters.city}
                onChange={(value) => setFilter('city', value)}
                options={[
                  { value: '', label: t("Toutes") },
                  ...(facets?.cities.map((row) => ({ value: row.city, label: t(row.city) })) ?? []),
                ]}
              />
            </Field>
          </div>
          <div className="filter--secondary" style={{ flex: '1 1 140px' }}>
            <Field label={t("Type de séjour")}>
              <Select
                value={filters.mode}
                onChange={(value) => setFilter('mode', value)}
                options={[
                  { value: '', label: t("Tous") },
                  ...Object.entries(MODE_LABEL).map(([value, label]) => ({ value, label })),
                ]}
              />
            </Field>
          </div>
          <div className="filter--secondary" style={{ flex: '1 1 130px' }}>
            <Field label={t("Budget max (DH)")}>
              <TextInput
                value={filters.maxPriceCentimes ? String(Number(filters.maxPriceCentimes) / 100) : ''}
                onChange={(value) => {
                  const digits = value.replace(/\D/g, '');
                  setFilter('maxPriceCentimes', digits ? String(Number(digits) * 100) : '');
                }}
                inputMode="numeric"
                placeholder="2 500"
              />
            </Field>
          </div>
          <div className="filter--secondary" style={{ flex: '1 1 140px' }}>
            <Field label={t("Trier par")}>
              <Select value={filters.sort} onChange={(value) => setFilter('sort', value)} options={SORTS} />
            </Field>
          </div>
        </div>

        <div className="row" style={{ gap: 8 }}>
          {/* A ghost button with an icon, not bare text: on a phone the old
              quiet style read as a label, and nobody tapped it. */}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setShowAllFilters((open) => !open)}
            aria-expanded={showAllFilters}
          >
            <FilterIcon className="icon-18" />
            <span className="only-desktop">
              {showAllFilters ? t("Moins de filtres") : t("Plus de filtres")}
              {!showAllFilters && advancedActive > 0 ? ` · ${advancedActive}` : ''}
            </span>
            <span className="only-mobile">
              {showAllFilters ? t("Masquer les filtres") : t("Tous les filtres")}
              {!showAllFilters && mobileHiddenActive > 0 ? ` · ${mobileHiddenActive}` : ''}
            </span>
          </button>
        </div>

        {showAllFilters ? (
        <div className="row row--wrap" style={{ gap: 10 }}>
          <Field label={t("Disponible du")}>
            <input
              className="input"
              type="date"
              min={today()}
              value={filters.startDate}
              onChange={(event) => setFilter('startDate', event.target.value)}
            />
          </Field>
          <Field label="au">
            <input
              className="input"
              type="date"
              min={filters.startDate || today()}
              value={filters.endDate}
              onChange={(event) => setFilter('endDate', event.target.value)}
            />
          </Field>
          <Field label={t("Voyageurs")}>
            <Select
              value={filters.guests}
              onChange={(value) => setFilter('guests', value)}
              options={[
                { value: '', label: t("Peu importe") },
                ...[1, 2, 3, 4, 5, 6].map((count) => ({
                  value: String(count),
                  label: t('{count} personne(s)', { count }),
                })),
              ]}
            />
          </Field>
          <Field label={t("Préférence")}>
            <Select
              value={filters.genderPolicy}
              onChange={(value) => setFilter('genderPolicy', value)}
              options={[
                { value: '', label: t("Peu importe") },
                ...Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label })),
              ]}
            />
          </Field>
          {facets?.universities.length ? (
            <Field label={t("Près de")}>
              <Select
                value={filters.universityId}
                onChange={(value) => {
                  setFilter('universityId', value);
                  if (value && !filters.maxDistanceMetres) setFilter('maxDistanceMetres', '3000');
                }}
                options={[
                  { value: '', label: t("Aucune université") },
                  ...facets.universities.map((university) => ({
                    value: university.id,
                    label: `${university.shortName ?? university.name} · ${t(university.city)}`,
                  })),
                ]}
              />
            </Field>
          ) : null}
        </div>
        ) : null}

        {showAllFilters ? (
        <div className="row row--wrap" style={{ gap: 8 }}>
          <button
            type="button"
            className={`chip ${filters.furnished === 'true' ? 'chip--on' : ''}`}
            onClick={() => setFilter('furnished', filters.furnished === 'true' ? '' : 'true')}
          >
            {t("Meublé")}
          </button>
          <button
            type="button"
            className={`chip ${filters.visitedOnly === 'true' ? 'chip--on' : ''}`}
            onClick={() => setFilter('visitedOnly', filters.visitedOnly === 'true' ? '' : 'true')}
          >
            {t("Visité par Bedly")}
          </button>
          <button
            type="button"
            className={`chip ${filters.verifiedHostOnly === 'true' ? 'chip--on' : ''}`}
            onClick={() =>
              setFilter('verifiedHostOnly', filters.verifiedHostOnly === 'true' ? '' : 'true')
            }
          >
            {t("Hôte vérifié")}
          </button>
          {ALL_AMENITIES.slice(0, 8).map((amenity) => (
            <button
              key={amenity}
              type="button"
              className={`chip ${selectedAmenities.includes(amenity) ? 'chip--on' : ''}`}
              onClick={() => toggleAmenity(amenity)}
            >
              {AMENITY_LABEL[amenity]}
            </button>
          ))}
        </div>
        ) : null}

        <div className="row row--between">
          <span className="small muted">
            {loading
              ? t("Recherche…")
              : t('{count} logement(s)', {
                  count: `${results?.items.length ?? 0}${results?.hasMore ? '+' : ''}`,
                })}
            {activeFilterCount > 0 ? ` · ${t('{count} filtre(s)', { count: activeFilterCount })}` : ''}
          </span>
          <div className="row" style={{ gap: 8 }}>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                {t("Réinitialiser")}
              </button>
            ) : null}
            {/* On a phone the floating button below does this job. */}
            <button
              type="button"
              className="btn btn--ghost btn--sm header__desktop-only"
              onClick={() => setShowMap((value) => !value)}
            >
              {showMap ? t("Vue liste") : t("Vue carte")}
            </button>
          </div>
        </div>
      </div>

      {results?.university ? (
        <Notice tone="info">
          {t("Résultats classés par distance à")} {t(results.university.name)}{t(". Les positions affichées sont approximatives à 300 m près.")}
        </Notice>
      ) : null}

      {showMap ? (
        <div className="stack stack--tight">
          <MapView
            pins={pins.map((pin) => ({
              id: pin.id,
              lat: pin.lat,
              lng: pin.lng,
              label: pin.title,
              priceCentimes: pin.priceFromCentimes,
            }))}
            height="var(--search-map-h)"
            onPinClick={(id) => navigate(`/logement/${id}`)}
          />
          <span className="tiny muted">
            {pins.length} annonce{pins.length === 1 ? '' : 's'} {t("· chaque cercle couvre environ 300 m autour du logement — l’adresse exacte n’est communiquée qu’après réservation.")}
          </span>
        </div>
      ) : loading ? (
        <div className="grid">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div key={index} className="skeleton" style={{ height: 280 }} />
          ))}
        </div>
      ) : results && results.items.length > 0 ? (
        <>
          <div className="grid">
            {results.items.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
          {results.hasMore ? (
            <div className="center">
              <button className="btn btn--ghost" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? t("Chargement…") : t("Afficher plus")}
              </button>
            </div>
          ) : null}
        </>
      ) : facets && facets.totalListings === 0 ? (
        /* Nothing is published at all — telling someone to widen their filters
           would be misleading, since no filter would have matched. */
        <EmptyState
          title={t("Aucune annonce en ligne pour le moment")}
          description={t("Bedly vient d’ouvrir. Les premières chambres et colocations arrivent — et si vous avez un logement à louer, vous pouvez être le premier à publier.")}
          action={
            <a className="btn" href="/devenir-hote">
              {t("Publier un logement")}
            </a>
          }
        />
      ) : (
        <EmptyState
          title={t("Aucun résultat")}
          description={t("Essayez d’élargir votre budget, de changer de ville ou de retirer quelques filtres.")}
          action={
            <button className="btn btn--ghost" onClick={() => setParams(new URLSearchParams())}>
              {t("Réinitialiser les filtres")}
            </button>
          }
        />
      )}

      {/*
        The phone version of the map toggle, the way people already know it from
        Airbnb: one pill, bottom centre, always within thumb reach, over whichever
        view is showing. Each pin on the map carries the listing's price.
      */}
      <button
        type="button"
        className="map-fab"
        onClick={() => {
          setShowMap((value) => !value);
          window.scrollTo({ top: 0 });
        }}
      >
        {showMap ? <ListIcon className="icon-18" /> : <MapIcon className="icon-18" />}
        {showMap ? t("Liste") : t("Carte")}
      </button>
    </div>
  );
}
